import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { Clock, CheckCircle, RefreshCw, Dumbbell, Apple, Shield, ArrowRight, AlertTriangle } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingApproval = () => {
  const navigate = useNavigate();
  const { user, refreshStatus, canAccessApp, getAssessmentStatus } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState({ workout: null, nutrition: null });
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans/my/current`);
      setPlans({
        workout: response.data.workout_plan,
        nutrition: response.data.nutrition_plan
      });
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async () => {
    setChecking(true);
    try {
      const status = await refreshStatus();
      
      if (status?.routing?.can_access_app) {
        toast.success('¡Tus planes han sido aprobados!');
        navigate('/app/dashboard');
      } else if (status?.plan?.status === 'approved') {
        // Plans approved but not activated yet
        await activatePlans();
      } else {
        toast.info('Tus planes aún están pendientes de aprobación');
        await loadPlans();
      }
    } catch (error) {
      toast.error('Error al verificar estado');
    } finally {
      setChecking(false);
    }
  };

  const activatePlans = async () => {
    try {
      await axios.post(`${API}/plans/activate`);
      toast.success('¡Planes activados! Bienvenido a ONOR');
      await refreshStatus();
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar planes');
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'pending_review':
        return { text: 'Pendiente de revisión', color: 'text-amber-400', icon: Clock };
      case 'approved':
        return { text: 'Aprobado', color: 'text-emerald-400', icon: CheckCircle };
      case 'changes_requested':
        return { text: 'Cambios solicitados', color: 'text-orange-400', icon: RefreshCw };
      case 'rejected':
        return { text: 'Rechazado', color: 'text-red-400', icon: Clock };
      case 'active':
        return { text: 'Activo', color: 'text-primary', icon: CheckCircle };
      default:
        return { text: 'Generando...', color: 'text-zinc-400', icon: Loader2 };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Empathetic in-progress / temporarily-stuck state. We no longer scream
  // "PROBLEMA" because the user has done nothing wrong: the AI is either
  // still working, retrying, or waiting for an admin to finalize. We keep
  // the user informed AND give them a way back to the main site, plus a
  // small CTA to check status without re-loading the page.
  if (getAssessmentStatus() === 'plans_failed') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-6">
              <img
                src="/brand/app-icon-square.webp"
                alt="Onor Code"
                className="h-16 w-16 rounded"
              />
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
              <span className="text-primary text-sm font-medium">PLAN EN PROCESO</span>
            </div>

            <h1 className="font-teko text-4xl sm:text-5xl text-white uppercase mb-4">
              ESTAMOS PREPARANDO TU PLAN
            </h1>
            <p className="text-zinc-300 font-manrope max-w-md mx-auto">
              Tu información se registró con éxito. Nuestro equipo de ONOR
              Coach está terminando los últimos detalles de tu plan personalizado.
            </p>
          </div>

          <Card className="card-glass border-primary/30 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Tiempo estimado: menos de 1 hora</h4>
                  <p className="text-zinc-400 text-sm font-manrope">
                    Recibirás una notificación en tu celular en cuanto tu plan esté listo.
                    Mientras tanto, puedes seguir explorando ONOR Code.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={checkStatus}
              variant="ghost"
              className="btn-secondary flex-1"
              disabled={checking}
              data-testid="check-status-btn"
            >
              {checking ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Consultando…</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Verificar estado</>
              )}
            </Button>
            <Button
              onClick={() => navigate('/app/dashboard')}
              className="btn-primary flex-1"
              data-testid="back-to-dashboard-btn"
            >
              Volver al inicio
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const workoutStatus = getStatusDisplay(plans.workout?.status);
  const nutritionStatus = getStatusDisplay(plans.nutrition?.status);

  // Check if both plans are approved - show activate button
  const bothApproved = plans.workout?.status === 'approved' && plans.nutrition?.status === 'approved';

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img 
              src="/brand/app-icon-square.webp" 
              alt="Onor Code" 
              className="h-16 w-16 rounded"
            />
          </div>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">PENDIENTE DE APROBACIÓN</span>
          </div>
          
          <h1 className="font-teko text-4xl sm:text-5xl text-white uppercase mb-4">
            TUS PLANES ONOR
          </h1>
          <p className="text-zinc-400 font-manrope max-w-md mx-auto">
            Tu información ha sido registrada con éxito. 
            Nuestro equipo está preparando tu plan personalizado.
            Te notificaremos cuando esté listo para comenzar.
          </p>
        </div>

        {/* Plan Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Workout Plan */}
          <Card className="card-glass border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Dumbbell className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-teko text-xl text-white">PLAN DE ENTRENAMIENTO</h3>
                  <p className="text-zinc-500 text-sm font-manrope">
                    {plans.workout?.title || 'Generando...'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <workoutStatus.icon className={`w-4 h-4 ${workoutStatus.color} ${plans.workout?.status === 'generating' ? 'animate-spin' : ''}`} />
                <span className={`text-sm font-medium ${workoutStatus.color}`}>
                  {workoutStatus.text}
                </span>
              </div>
              
              {plans.workout && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-zinc-500">Duración</span>
                      <p className="text-white font-medium">{plans.workout.weeks || 4} semanas</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Días/semana</span>
                      <p className="text-white font-medium">{plans.workout.days_per_week || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Nutrition Plan */}
          <Card className="card-glass border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                  <Apple className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-teko text-xl text-white">PLAN NUTRICIONAL</h3>
                  <p className="text-zinc-500 text-sm font-manrope">
                    {plans.nutrition?.title || 'Generando...'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <nutritionStatus.icon className={`w-4 h-4 ${nutritionStatus.color} ${plans.nutrition?.status === 'generating' ? 'animate-spin' : ''}`} />
                <span className={`text-sm font-medium ${nutritionStatus.color}`}>
                  {nutritionStatus.text}
                </span>
              </div>
              
              {plans.nutrition && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-zinc-500">Calorías/día</span>
                      <p className="text-white font-medium">{plans.nutrition.caloric_target || '-'} kcal</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Comidas</span>
                      <p className="text-white font-medium">{plans.nutrition.meal_frequency || '-'}/día</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="card-glass border-zinc-800 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-2">¿Por qué necesitan aprobación?</h4>
                <p className="text-zinc-400 text-sm font-manrope">
                  En ONOR, cada plan es revisado por nuestro equipo de profesionales para garantizar 
                  que sea seguro y apropiado para tu perfil y objetivos. Este proceso asegura que 
                  recibas un plan de calidad adaptado a tus necesidades.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons — always allow the user to leave the page.
          * The previous version only rendered "ACTIVAR MIS PLANES" when
          * both plans were approved, leaving the user trapped on this
          * screen with no escape if they were still pending. */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {bothApproved && (
            <Button
              onClick={activatePlans}
              className="btn-primary px-8"
              data-testid="activate-plans-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              ACTIVAR MIS PLANES
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          <Button
            onClick={checkStatus}
            className="btn-secondary px-8"
            disabled={checking}
            data-testid="check-status-btn"
          >
            {checking ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                VERIFICANDO...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                VERIFICAR ESTADO
              </>
            )}
          </Button>
          <Button
            onClick={() => navigate('/app/dashboard')}
            variant={bothApproved ? 'ghost' : 'default'}
            className={bothApproved ? 'btn-secondary px-8' : 'btn-primary px-8'}
            data-testid="back-to-dashboard-btn"
          >
            Volver al inicio
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Footer note */}
        <p className="text-center text-zinc-600 text-xs mt-8 font-manrope">
          Recibirás una notificación cuando tus planes sean aprobados.
          <br />
          El tiempo de revisión típico es menor a 1 hora.
        </p>
      </div>
    </div>
  );
};

export default PendingApproval;
